import { useEffect, RefObject } from 'react';

export const useFocusTrap = ({
  trapRef,
  onClose,
  enabled = true,
}: {
  trapRef: RefObject<HTMLElement | null>;
  onClose?: () => void;
  enabled?: boolean;
}) => {
  useEffect(() => {
    if (!enabled || !trapRef?.current) return;

    const container = trapRef.current;
    const prevFocusedEl = document.activeElement as HTMLElement | null;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
    ];

    const getFocusableElements = (): HTMLElement[] => {
      return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter(el => {
        const isHidden = el.closest('[aria-hidden="true"]') !== null;
        const style = window.getComputedStyle(el);
        const isVisible =
          el.offsetParent !== null && style.visibility !== 'hidden' && style.display !== 'none' && parseFloat(style.opacity) > 0;
        return !isHidden && isVisible;
      });
    };

    const focusableEls = getFocusableElements();
    if (focusableEls.length) focusableEls[0].focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' && e.key !== 'Escape') return;

      const els = getFocusableElements();

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        if (prevFocusedEl && document.contains(prevFocusedEl)) {
          prevFocusedEl.focus();
        }
        return;
      }

      if (!els.length) return;

      e.preventDefault();

      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      const activeIndex = els.findIndex(el => el.contains(document.activeElement));

      if (e.shiftKey) {
        const prevEl = els[activeIndex - 1] || lastEl;
        prevEl.focus();
        return;
      }

      const nextEl = els[activeIndex + 1] || firstEl;
      nextEl.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (prevFocusedEl && document.contains(prevFocusedEl)) {
        prevFocusedEl.focus();
      }
    };
  }, [trapRef, enabled, onClose]);
};
